// src/components/InstallPrompt.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloseIcon from '@mui/icons-material/Close';
import theme from '../theme';

const DISMISSED_KEY = 'cp:pwa_dismissed';

export default function InstallPrompt() {
  const [show, setShow] = useState(false);
  const deferredRef = useRef(null);

  useEffect(() => {
    try { if (localStorage.getItem(DISMISSED_KEY)) return; } catch {}
    // Already installed
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) return;

    const handler = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = useCallback(async () => {
    if (!deferredRef.current) return;
    deferredRef.current.prompt();
    const result = await deferredRef.current.userChoice;
    if (result.outcome === 'accepted') setShow(false);
    deferredRef.current = null;
  }, []);

  const dismiss = useCallback(() => {
    setShow(false);
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch {}
  }, []);

  if (!show) return null;

  return (
    <Box sx={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 1200, maxWidth: 400, width: 'calc(100% - 32px)',
    }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          p: 1.5,
          borderRadius: theme.radii.card,
          bgcolor: theme.colors.card,
          border: `1px solid ${theme.colors.primary}44`,
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
        }}
      >
        <GetAppIcon sx={{ color: theme.colors.primary, fontSize: 28 }} />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: theme.colors.text }}>Install ChainPlay</Typography>
          <Typography sx={{ fontSize: '0.7rem', color: theme.colors.subText }}>Add to home screen for the best experience</Typography>
        </Stack>
        <Button
          onClick={install}
          variant="contained"
          size="small"
          sx={{
            bgcolor: theme.colors.primary, color: '#000', fontWeight: 900,
            borderRadius: theme.radii.button, textTransform: 'none', px: 2,
            '&:hover': { bgcolor: theme.colors.primaryHover },
          }}
          aria-label="Install ChainPlay app"
        >
          Install
        </Button>
        <IconButton onClick={dismiss} size="small" sx={{ color: theme.colors.subText }} aria-label="Dismiss install prompt">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
